import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getTest, previewSubmitTest, publishTest } from "@/lib/api";
import { AdminSideNav } from "@/pages/admin/AdminDashboardPage";
import BrandedHeader from "@/components/BrandedHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Brain, ArrowLeft, CheckCircle, XCircle, Loader2, Rocket, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function AdminTestPreviewPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);

  useEffect(() => { loadTest(); }, [testId]);

  const loadTest = async () => {
    try {
      const res = await getTest(testId);
      setTest(res.data);
    } catch (e) { toast.error("Failed to load test"); }
    finally { setLoading(false); }
  };

  const handleSelect = (qIdx, option) => {
    setAnswers({ ...answers, [String(qIdx)]: option });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await previewSubmitTest(testId, answers);
      setResults(res.data);
    } catch (e) { toast.error("Failed to submit"); }
    finally { setSubmitting(false); }
  };

  const handleReset = () => {
    setAnswers({});
    setResults(null);
    setCurrentQ(0);
  };

  const handlePublish = async () => {
    try {
      await publishTest(testId);
      toast.success("Test published!");
      navigate('/admin/tests');
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to publish"); }
  };

  if (loading) {
    return (<div className="min-h-screen bg-background flex"><AdminSideNav active="Tests" /><div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div></div>);
  }
  if (!test) return null;

  const questions = test.questions || [];
  const totalQ = questions.length;
  const answered = Object.keys(answers).length;

  // Results view
  if (results) {
    return (
      <div className="min-h-screen bg-background flex">
        <AdminSideNav active="Tests" />
        <div className="flex-1 flex flex-col">
          <header className="px-4 sm:px-6 lg:px-8 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/tests')}><ArrowLeft className="w-4 h-4" /></Button>
              <img 
                src="/branding/securado-logo-white.png" 
                alt="Securado" 
                className="h-6 w-auto object-contain"
              />
              <span className="font-display font-semibold">Test Preview — Results</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReset}><RotateCcw className="w-4 h-4 mr-1" /> Retake</Button>
              <Button onClick={handlePublish} className="btn-press" data-testid="publish-from-preview-button"><Rocket className="w-4 h-4 mr-1" /> Publish Test</Button>
            </div>
          </header>
          <div className="px-4 sm:px-6 lg:px-8 py-6 flex-1 overflow-auto">
            <div className="max-w-3xl mx-auto">
              {/* Score Banner */}
              <Card className={`border mb-6 ${results.passed ? 'border-[hsl(var(--success))]/25 bg-[hsl(var(--success))]/5' : 'border-[hsl(var(--destructive))]/25 bg-[hsl(var(--destructive))]/5'}`} data-testid="preview-results-banner">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-display font-semibold mb-1">{results.score_pct}%</p>
                  <p className="text-muted-foreground">{results.correct}/{results.total} correct</p>
                  <Badge className={`mt-2 text-sm ${results.passed ? 'bg-[hsl(var(--success))] text-white' : 'bg-[hsl(var(--destructive))] text-white'}`}>
                    {results.passed ? 'PASSED' : 'FAILED'} (pass: {results.passing_score}%)
                  </Badge>
                </CardContent>
              </Card>

              {/* Detailed Results */}
              <div className="space-y-4">
                {results.results.map((r, i) => (
                  <Card key={i} className={`border ${r.is_correct ? 'border-[hsl(var(--success))]/20' : 'border-[hsl(var(--destructive))]/20'} bg-card`}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3">
                        {r.is_correct ? <CheckCircle className="w-5 h-5 text-[hsl(var(--success))] mt-0.5 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-[hsl(var(--destructive))] mt-0.5 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium mb-2">Q{i + 1}. {r.question_text}</p>
                          <div className="flex items-center gap-3 text-xs">
                            <span>Your answer: <strong className={r.is_correct ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--destructive))]'}>{r.user_answer || '—'}</strong></span>
                            {!r.is_correct && <span>Correct: <strong className="text-[hsl(var(--success))]">{r.correct_answer}</strong></span>}
                          </div>
                          {r.explanation && <p className="text-xs text-muted-foreground mt-2 italic">{r.explanation}</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Test-taking view
  const q = questions[currentQ];
  return (
    <div className="min-h-screen bg-background flex">
      <AdminSideNav active="Tests" />
      <div className="flex-1 flex flex-col">
        <header className="px-4 sm:px-6 lg:px-8 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/tests')}><ArrowLeft className="w-4 h-4" /></Button>
            <img 
              src="/branding/securado-logo-white.png" 
              alt="Securado" 
              className="h-6 w-auto object-contain"
            />
            <span className="font-display font-semibold">Testing: {test.title}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{answered}/{totalQ} answered</span>
            <Button onClick={handleSubmit} disabled={submitting || answered === 0} className="btn-press" data-testid="preview-submit-button">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Submit Preview
            </Button>
          </div>
        </header>

        <div className="px-4 sm:px-6 lg:px-8 py-6 flex-1 overflow-auto">
          <div className="max-w-3xl mx-auto">
            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between text-xs text-muted-foreground mb-2">
                <span>Question {currentQ + 1} of {totalQ}</span>
                <span>{Math.round((answered / totalQ) * 100)}% complete</span>
              </div>
              <Progress value={(answered / totalQ) * 100} className="h-1.5" />
            </div>

            {/* Question Navigation */}
            <div className="flex flex-wrap gap-1.5 mb-6">
              {questions.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentQ(i)}
                  className={`w-8 h-8 rounded-lg text-xs font-medium flex items-center justify-center transition-colors ${
                    i === currentQ ? 'bg-primary text-primary-foreground' :
                    answers[String(i)] ? 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border border-[hsl(var(--success))]/25' :
                    'bg-secondary text-muted-foreground hover:bg-secondary/80'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            {/* Current Question */}
            {q && (
              <Card className="border-border bg-card" data-testid="preview-question-card">
                <CardContent className="p-6">
                  {q.scenario && (
                    <div className="bg-secondary/50 rounded-lg p-4 mb-4 text-sm text-muted-foreground italic">
                      {q.scenario}
                    </div>
                  )}
                  <h3 className="text-base font-medium mb-5">Q{currentQ + 1}. {q.question_text}</h3>
                  <div className="space-y-2">
                    {q.options.map((opt) => (
                      <button
                        key={opt.label}
                        onClick={() => handleSelect(currentQ, opt.label)}
                        className={`w-full text-left rounded-lg border p-4 text-sm transition-colors ${
                          answers[String(currentQ)] === opt.label
                            ? 'border-primary bg-primary/10 text-foreground'
                            : 'border-border bg-card hover:border-primary/30 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <span className="font-semibold mr-3">{opt.label}.</span>
                        {opt.text}
                      </button>
                    ))}
                  </div>
                  {/* Nav */}
                  <div className="flex justify-between mt-6">
                    <Button variant="outline" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}>Previous</Button>
                    {currentQ < totalQ - 1 ? (
                      <Button onClick={() => setCurrentQ(currentQ + 1)}>Next</Button>
                    ) : (
                      <Button onClick={handleSubmit} disabled={submitting || answered === 0} className="btn-press">
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Submit
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
